import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  inject,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { PermissionService } from '@app/core/services/permission.service';

import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

import {
  STOCK_SEARCH_CONFIG,
  STOCK_TABLE_COLUMNS,
  STOCK_SEARCH_DEFAULT_COLUMNS_ORDER,
  STOCK_SEARCH_FLEXIBLE_COLUMNS_ORDER,
  STOCK_SEARCH_REFERENCE_COLUMNS_ORDER,
  StockItem,
} from './stock-search.config';
import { StockSearchService } from './services/stock-search.service';
import { BASE_PATH } from '@app/api/variables';
import { environment } from '@environments/environment';
import { GoodsStockControllerService, InventoryStatusControllerService } from '@app/api';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
  WarehouseQueryResVO,
} from '@app/api/stock-service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';
import { of, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { PurchaseRequestDataService } from '@app/feature/center-business/procurement/purchase-request/services/purchase-request-data.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import type { FormFieldConfig } from '@shared/components/search-form/search-form.component';

@Component({
  selector: 'app-stock-search',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TranslateModule,
    TooltipModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    AppButtonComponent,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    StockSearchService,
    GoodsStockControllerService,
    InventoryStatusControllerService,
    WarehouseControllerService,
  ],

  templateUrl: './stock-search.component.html',
  styleUrls: ['./stock-search.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockSearchComponent implements OnInit {
  // Search configuration
  searchConfig: FormFieldConfig[] = this.buildSearchConfig();

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of STOCK_SEARCH_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of STOCK_SEARCH_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  referenceColumnOrder = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of STOCK_SEARCH_REFERENCE_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }
    return cols;
  });

  // Data management
  filteredStockItems = signal<StockItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  selectedItems = signal<StockItem[]>([]);

  // Search parameters (using 1-based pagination like brand component)
  searchParams = signal({
    page: 1, // Use 1-based pagination
    size: 20,
  });

  // Current active filter criteria - persisted across page changes
  private readonly currentFilters = signal<any>(null);

  // Flag to prevent double API calls
  private isSearching = false;

  // Computed signal for button enabled state
  hasSelectedItems = computed(() => this.selectedItems().length > 0);

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'inventory_lookup' for inventory permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inventory_lookup.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inventory_lookup.create');
  });

  private readonly router = inject(Router);
  private readonly purchaseRequestDataService = inject(
    PurchaseRequestDataService,
  );
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);

  constructor(
    private readonly stockService: StockSearchService,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.loadStockData();
    this.loadCategoryOptions();
  }

  private buildSearchConfig(): FormFieldConfig[] {
    return withPaginatedSearchFields(STOCK_SEARCH_CONFIG, {
      warehouseCode: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            status: true,
            valueKey: 'code',
          }),
      },
    });
  }

  private loadStockData(): void {
    this.loading.set(true);

    this.stockService
      .getStockItems(
        this.searchParams().page,
        this.searchParams().size,
        this.currentFilters() ?? undefined,
      )
      .subscribe({
        next: (response) => {
          this.filteredStockItems.set(response.data);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading stock data:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load stock data. Please try again.',
          });
          this.loading.set(false);
          this.filteredStockItems.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onSearch(searchCriteria: any): void {
    if (this.isSearching) return; // Prevent double calls

    this.isSearching = true;
    this.loading.set(true);

    // Clear selection when searching
    this.selectedItems.set([]);

    // Clean up search criteria - remove empty values
    const cleanCriteria = this.cleanSearchCriteria(searchCriteria);

    // Persist filters so pagination reuses them
    this.currentFilters.set(cleanCriteria);
    this.searchParams.set({ ...this.searchParams(), page: 1 });

    this.stockService
      .getStockItems(
        1, // Reset to first page
        this.searchParams().size,
        cleanCriteria,
      )
      .subscribe({
        next: (response) => {
          this.filteredStockItems.set(response.data);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);
          this.isSearching = false;
        },
        error: (error) => {
          console.error('Error searching stock data:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to search stock data. Please try again.',
          });
          this.loading.set(false);
          this.isSearching = false;
          this.filteredStockItems.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onReset(): void {
    this.currentFilters.set(null);
    this.searchParams.set({
      page: 1,
      size: 20,
    });
    this.selectedItems.set([]);
    this.loadStockData();
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    // Clear selection when page changes
    this.selectedItems.set([]);
    this.loadStockData();
  }

  onViewDetails(record: StockItem): void {
    const recordId = record.id || record.skuCode || 'unknown';
    this.router.navigate(['/stock/search/view', recordId]);
  }

  onSelectionChange(selected: StockItem[]): void {
    this.selectedItems.set(selected);
  }

  onCreatePurchaseRequisition(): void {
    if (this.selectedItems().length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail:
          'Please select at least one product to create a purchase requisition',
      });
      return;
    }

    // Store selected items in the shared service
    // The StockItem includes skuName from API response even though it's not displayed in table
    this.purchaseRequestDataService.setSelectedStockItems(this.selectedItems());

    // Navigate to purchase requisition add page
    this.router.navigate(['/procurement/purchase-request/add']);
  }

  // Clean up search criteria by removing empty/null values
  private cleanSearchCriteria(criteria: any): any {
    const cleanCriteria: any = {};

    Object.keys(criteria).forEach((key) => {
      const value = criteria[key];

      // Keep non-empty values
      if (value !== null && value !== undefined && value !== '') {
        // For arrays, keep if they have items
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleanCriteria[key] = value;
          }
        } else {
          cleanCriteria[key] = value;
        }
      }
    });

    return cleanCriteria;
  }

  // Load category options from API
  private loadCategoryOptions(): void {
    this.stockService.getCategoryTreeOptions({ size: 20 }).subscribe({
      next: (categories) => {
        // Update the search config with real category data
        this.searchConfig = this.searchConfig.map((field) =>
          field.key === 'categoryIdList'
            ? { ...field, options: categories }
            : field,
        );
      },
      error: (error) => {
        console.error('Error loading category options:', error);
      },
    });
  }


  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }
}
